import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { errMsg } from "@/lib/api";
import { Avatar } from "@/components/chat/Avatar";

const ParticipantAvatar = ({ p }) => {
  const { api } = useOutletContext();
  return <Avatar client={api} path={p.avatar_file_id ? `/admin/files/${p.avatar_file_id}` : null} name={p.name} className="h-14 w-14 text-lg" testId="details-avatar" />;
};
import { fmtDateTime, maskPhone, waLink, STATUS_LABEL, KIND_LABEL } from "@/lib/format";

const Section = ({ title, children }) => (
  <div className="space-y-2 border-b border-zap-line px-5 py-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
    {children}
  </div>
);

const EVENT_LABEL = {
  cadastro: "Cadastro", recuperacao: "Acesso recuperado", reentrada_sem_codigo: "Entrou de novo com nome e WhatsApp", mensagem_apagada: "Mensagem apagada", mensagem_corrigida: "Mensagem corrigida", mensagem_ocultada: "Apagou uma mensagem da equipe só para si", foto_perfil: "Atualizou a foto", transmissao_recebida: "Recebeu transmissão", recuperacao_falhou: "Tentativa de recuperação falhou", envio: "Envio",
  resposta: "Resposta da equipe", atendimento_atualizado: "Atendimento atualizado", pedido_exclusao: "Pediu exclusão", pedido_correcao: "Pediu correção",
  sessoes_encerradas: "Sessões encerradas", novo_codigo: "Novo código de recuperação", situacao: "Situação alterada",
  classificacao: "Classificação automática", classificacao_manual: "Categoria alterada",
  resposta_interna: "Resposta interna", resposta_whatsapp_preparada: "Preparada para WhatsApp", whatsapp_enviado: "WhatsApp: envio realizado",
  whatsapp_nao_enviado: "WhatsApp: não enviado", whatsapp_retornar_depois: "WhatsApp: retornar depois",
};

const Activity = ({ p, version }) => {
  const { api } = useOutletContext();
  const [a, setA] = useState(null);
  const load = () => api.get(`/admin/participants/${p.id}/activity`).then((r) => setA(r.data)).catch(() => {});
  useEffect(() => { load(); }, [p.id, p.status, p.last_message_at, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const revoke = async () => {
    try { const { data } = await api.post(`/admin/participants/${p.id}/revoke-sessions`); toast.success(`${data.revoked} sessão(ões) encerrada(s).`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!a) return null;
  const o = a.origins[a.origins.length - 1] || p.origin || {};
  return (
    <>
      <Section title="Origem e sessões">
        <p data-testid="details-origin" className="text-sm">Origem: <span className="font-medium">{o.source || "—"}</span>{o.campaign ? ` · ${o.campaign}` : ""}{o.referrer ? ` · ${o.referrer}` : ""}</p>
        <p data-testid="details-sessions" className="text-sm">Aparelhos conectados: <span className="font-semibold">{a.active_sessions}</span> · Atendimentos: {a.attendances.length}</p>
        <Button data-testid="details-revoke-sessions" size="sm" variant="outline" disabled={!a.active_sessions} onClick={revoke}>Encerrar sessões</Button>
      </Section>
      <Section title="Registro de atividades">
        <ul data-testid="details-history" className="space-y-1 text-sm">
          {a.history.slice(0, 8).map((h) => (
            <li key={h.id} className="flex justify-between gap-2"><span>{EVENT_LABEL[h.kind] || h.kind}</span><span className="shrink-0 text-xs text-muted-foreground">{fmtDateTime(h.at)}</span></li>
          ))}
        </ul>
      </Section>
    </>
  );
};

const RecoveryCode = ({ p }) => {
  const { api } = useOutletContext();
  const [code, setCode] = useState(null);
  const gen = async () => {
    try { const { data } = await api.post(`/admin/participants/${p.id}/recovery-code`); setCode(data.recovery_code); } catch (e) { toast.error(errMsg(e)); }
  };
  const text = `Olá, ${p.name.split(" ")[0]}! Seu novo código de recuperação do ZAP IUDP é: ${code}. Use-o em "Já tenho cadastro" → "Tenho o código de recuperação", junto com seu WhatsApp. Ou entre só com o mesmo nome e WhatsApp do cadastro.`;
  return code ? (
    <div className="space-y-2">
      <p data-testid="recovery-code-value" className="rounded-md bg-zap-goldsoft px-3 py-2 text-center font-mono text-lg font-semibold tracking-widest">{code}</p>
      <a data-testid="recovery-code-wa" href={waLink(p.whatsapp, text)} target="_blank" rel="noreferrer" className="block text-center text-sm font-semibold text-zap-green hover:underline">Enviar pelo WhatsApp</a>
      <p className="text-xs text-muted-foreground">O código anterior deixou de valer.</p>
    </div>
  ) : (
    <Button data-testid="recovery-code-generate" variant="outline" className="w-full" onClick={gen}><KeyRound className="mr-2 h-4 w-4" /> Gerar novo código</Button>
  );
};

export const ParticipantDetails = ({ p, messages, onChanged, onDeleted }) => {
  const { api, me } = useOutletContext();
  const [notes, setNotes] = useState(p.notes || "");
  const [assigned, setAssigned] = useState(p.assigned_to || "");
  useEffect(() => { setNotes(p.notes || ""); setAssigned(p.assigned_to || ""); }, [p.id, p.notes, p.assigned_to]);
  const patch = async (body, okMsg) => {
    try { await api.patch(`/admin/participants/${p.id}`, body); onChanged(); if (okMsg) toast.success(okMsg); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/participants/${p.id}`); toast.success("Dados do participante excluídos."); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = messages.filter((m) => m.sender === "participant" && !m.deleted_at).reduce((a, m) => ({ ...a, [m.kind]: (a[m.kind] || 0) + 1 }), {});
  return (
    <div data-testid="participant-details">
      <Section title="Cadastro">
        <ParticipantAvatar p={p} />
        <p className="text-[16px] font-semibold">{p.name}</p>
        <p className="text-sm">{maskPhone(p.whatsapp)}</p>
        <p className="text-sm">{p.city} / {p.state}</p>
        <p className="text-xs text-muted-foreground">Cadastro em {fmtDateTime(p.created_at)} · Consentimento LGPD em {fmtDateTime(p.consent_at)}</p>
        {p.deletion_requested_at && <p data-testid="details-deletion-requested" className="rounded bg-red-50 px-2 py-1 text-sm font-medium text-red-800">Pediu exclusão em {fmtDateTime(p.deletion_requested_at)}</p>}
        {p.correction_requested_at && <p data-testid="details-correction-requested" className="rounded bg-zap-goldsoft px-2 py-1 text-sm">Pediu correção em {fmtDateTime(p.correction_requested_at)}: “{p.correction_details}”</p>}
      </Section>
      <Section title="Atendimento">
        <Select value={p.status} onValueChange={(v) => patch({ status: v }, "Situação atualizada.")}>
          <SelectTrigger data-testid="details-status"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {p.status_changed_at && <p data-testid="details-status-changed" className="text-xs text-muted-foreground">Alterada em {fmtDateTime(p.status_changed_at)}{p.status_changed_by ? ` por ${p.status_changed_by}` : ""}</p>}
        <Input data-testid="details-assigned" value={assigned} onChange={(e) => setAssigned(e.target.value)} onBlur={() => assigned !== (p.assigned_to || "") && patch({ assigned_to: assigned }, "Responsável atualizado.")} placeholder="Responsável" />
        <Textarea data-testid="details-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Anotações internas (não aparecem para o participante)" />
        <Button data-testid="details-notes-save" size="sm" variant="secondary" disabled={notes === (p.notes || "")} onClick={() => patch({ notes }, "Anotações salvas.")}>Salvar anotações</Button>
      </Section>
      <Section title="Histórico">
        {Object.keys(counts).length === 0 ? <p className="text-sm text-muted-foreground">Sem envios.</p> :
          <ul className="space-y-1 text-sm">{Object.entries(counts).map(([k, n]) => <li key={k} className="flex justify-between"><span>{KIND_LABEL[k]}</span><span className="font-semibold">{n}</span></li>)}</ul>}
      </Section>
      <Activity p={p} version={messages.map((m) => `${m.classification?.at || ""}${(m.category_ids || []).join()}`).join("|")} />
      <Section title="Recuperação de acesso"><RecoveryCode p={p} /></Section>
      {me.role === "admin" && (
        <Section title="LGPD">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="details-delete" variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50"><Trash2 className="mr-2 h-4 w-4" /> Excluir dados do participante</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir definitivamente?</AlertDialogTitle>
                <AlertDialogDescription>Cadastro, mensagens e arquivos de {p.name} deixam de existir no painel. Esta ação não pode ser desfeita.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="details-delete-confirm" onClick={remove} className="bg-red-700 hover:bg-red-800">Excluir</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>
      )}
    </div>
  );
};
